// ---------------------------------------------------------------------------
// JobIdentityCard — the "Job" section card. Four fields: Job name (the
// template's URL identifier), Task name (only shown when there's exactly
// one task — i.e. not compare-tasks), Display name, and Description.
//
// Field disabling is per-mode rather than via fieldset[disabled] (which
// the form uses for the rest of the form): jobName/taskName are
// server-immutable so they're always locked outside create mode, while
// displayName/description follow the metadataDisabled flag (view mode
// only) so edit mode can mutate them.
// ---------------------------------------------------------------------------

import { Field, Input } from '@fluentui/react-components';

import { ExpandableTextarea } from '../../../../../components/ExpandableTextarea/index.js';
import {
  EVALUATION_TEMPLATE_NAME_PATTERN,
  isValidEvaluationTemplateName,
} from '../../../../../types/evaluationTemplate.js';
import { useStyles } from '../../EvaluationTemplateForm.style.js';
import {
  TASK_NAME_PATTERN,
  type EvaluationMode,
} from '../../EvaluationTemplateForm.types.js';
import { isValidTaskName } from '../../EvaluationTemplateForm.utils.js';

export interface JobIdentityCardProps {
  mode: 'create' | 'view' | 'edit';
  /** True in view mode — disables displayName / description specifically. */
  metadataDisabled: boolean;
  evalMode: EvaluationMode;
  jobName: string;
  onJobNameChange: (s: string) => void;
  jobDisplayName: string;
  onJobDisplayNameChange: (s: string) => void;
  jobDescription: string;
  onJobDescriptionChange: (s: string) => void;
  /** Single-task name (`compare-tasks` hides this field — its tasks each
   *  carry their own name in the Tasks card). */
  taskName: string;
  onTaskNameChange: (s: string) => void;
}

export function JobIdentityCard({
  mode,
  metadataDisabled,
  evalMode,
  jobName,
  onJobNameChange,
  jobDisplayName,
  onJobDisplayNameChange,
  jobDescription,
  onJobDescriptionChange,
  taskName,
  onTaskNameChange,
}: JobIdentityCardProps) {
  const styles = useStyles();
  const namesLocked = mode !== 'create';

  return (
    <section className={styles.card} aria-labelledby="card-job">
      <div id="card-job" className={styles.cardLabel}>
        Job
        <span className={styles.cardLabelHelp}>
          Required by the API but not in the original mockup — name + display
          name + description.
        </span>
      </div>
      <div className={styles.cardBody}>
        <div className={styles.twoColumnFields}>
          <Field
            label="Job name"
            required
            hint="Lowercase identifier (e.g. my-eval-job). Cannot be changed later."
            validationState={
              jobName.length > 0 && !isValidEvaluationTemplateName(jobName)
                ? 'error'
                : 'none'
            }
            validationMessage={
              jobName.length > 0 && !isValidEvaluationTemplateName(jobName)
                ? 'Use lowercase letters, digits and hyphens. Must start with a letter.'
                : undefined
            }
          >
            <Input
              value={jobName}
              onChange={(_e, data) => onJobNameChange(data.value)}
              placeholder="my-job"
              pattern={EVALUATION_TEMPLATE_NAME_PATTERN.source}
              disabled={namesLocked}
            />
          </Field>
          {evalMode !== 'compare-tasks' && (
            <Field
              label="Task name"
              required
              hint="Lowercase identifier (e.g. quality-check)"
              validationState={
                taskName.length > 0 && !isValidTaskName(taskName)
                  ? 'error'
                  : 'none'
              }
              validationMessage={
                taskName.length > 0 && !isValidTaskName(taskName)
                  ? 'Use lowercase letters, digits and hyphens.'
                  : undefined
              }
            >
              <Input
                value={taskName}
                onChange={(_e, data) => onTaskNameChange(data.value)}
                placeholder="my-task"
                pattern={TASK_NAME_PATTERN.source}
                disabled={namesLocked}
              />
            </Field>
          )}
        </div>
        <Field label="Display name">
          <Input
            value={jobDisplayName}
            onChange={(_e, data) => onJobDisplayNameChange(data.value)}
            placeholder="My eval job"
            disabled={metadataDisabled}
          />
        </Field>
        <ExpandableTextarea
          label="Description"
          value={jobDescription}
          onChange={onJobDescriptionChange}
          rows={2}
          placeholder="Human-readable description of what this eval measures"
          disabled={metadataDisabled}
        />
      </div>
    </section>
  );
}
